import { useQuery, useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import type { Row } from '../lib/db-types'
import { useUserScopedCrud } from './useUserScopedCrud'

// User-facing shape: we never need `user_id`/`created_at` in the UI.
export type Category = Pick<Row<'categories'>, 'id' | 'name' | 'color' | 'sort_order'>
export type CategoryAssignment = Pick<
  Row<'category_assignments'>,
  'id' | 'category_id' | 'contact_profile_urn'
>

// --- Category definitions ----------------------------------------------------

/**
 * Categories (folders) are user-scoped by design — personal organisation,
 * never shared across a team. Only snippets (templates) have a team
 * dimension.
 */
export const categoriesKey = (userId: string | undefined): QueryKey => ['categories', userId ?? 'anon']

type CategoryUpdate = Partial<Pick<Category, 'name' | 'color' | 'sort_order'>>

export function useCategories() {
  // Thin adapter over useUserScopedCrud — keeps the public API
  // (addCategory / updateCategory / deleteCategory) stable so call sites
  // didn't need to change when we consolidated with useTags.
  const crud = useUserScopedCrud<Category, { name: string; color: string }, CategoryUpdate>({
    table: 'categories',
    select: 'id, name, color, sort_order',
    queryKey: categoriesKey,
    rowForInsert: ({ name, color }, userId, nextSortOrder) => ({
      user_id: userId,
      name,
      color,
      sort_order: nextSortOrder,
    }),
    rowForUpdate: (updates) => ({ ...updates }),
    // Instant-appear in the folder list; temp id swapped on server ack.
    optimisticRow: ({ name, color }, tempId, nextSortOrder) => ({
      id: tempId,
      name,
      color,
      sort_order: nextSortOrder,
    }),
  })

  return {
    categories: crud.items,
    loading: crud.loading,
    error: crud.error,
    // Callers `await` these and can .catch() — or wrap in try/catch for
    // toast surfacing. Not silently swallowed.
    addCategory: (name: string, color: string) => crud.add({ name, color }),
    updateCategory: (id: string, updates: CategoryUpdate) => crud.update(id, updates),
    deleteCategory: (id: string) => crud.remove(id),
  }
}

// --- Category assignments (contact ↔ category mapping) -----------------------

export const categoryAssignmentsKey = (
  userId: string | undefined,
  contactProfileUrn: string | undefined,
): QueryKey => ['categoryAssignments', userId ?? 'anon', contactProfileUrn ?? 'all']

export function useCategoryAssignments(contactProfileUrn?: string) {
  const { user } = useAuth()
  const qc = useQueryClient()
  const key = categoryAssignmentsKey(user?.id, contactProfileUrn)

  const query = useQuery<CategoryAssignment[]>({
    queryKey: key,
    enabled: !!user,
    queryFn: async () => {
      let q = supabase.from('category_assignments').select('id, category_id, contact_profile_urn')
      if (contactProfileUrn) q = q.eq('contact_profile_urn', contactProfileUrn)
      const { data, error } = await q
      if (error) throw new Error(error.message)
      return (data ?? []) as CategoryAssignment[]
    },
  })

  // Realtime invalidation is handled centrally by <RealtimeBridge />.
  // The bridge invalidates `['categoryAssignments', userId]`; TQ's prefix
  // matching covers per-contact sub-keys automatically.

  // Contract: both mutations THROW on Supabase error (matches
  // useBookmarks/useReminders/useTags). Consumers that wrap these
  // through `voidAsync` now see the rejection and log it; previously
  // the `{error}` return shape resolved successfully even on DB
  // failure, so voidAsync never saw anything to log. PR 2 of code-
  // review handoff.
  const assignMut = useMutation({
    mutationKey: ['categoryAssignments', 'assign'],
    mutationFn: async ({ categoryId, profileUrn }: { categoryId: string; profileUrn: string }) => {
      if (!user) throw new Error('Not authenticated')
      const { error } = await supabase
        .from('category_assignments')
        .insert({ user_id: user.id, category_id: categoryId, contact_profile_urn: profileUrn })
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoryAssignments', user?.id] })
    },
  })

  const removeMut = useMutation({
    mutationKey: ['categoryAssignments', 'remove'],
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('category_assignments').delete().eq('id', id)
      if (error) throw new Error(error.message)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['categoryAssignments', user?.id] })
    },
  })

  return {
    assignments: query.data ?? [],
    loading: !!user && query.isPending,
    error: query.error instanceof Error ? query.error.message : null,
    assignCategory: async (categoryId: string, profileUrn: string) =>
      assignMut.mutateAsync({ categoryId, profileUrn }),
    removeAssignment: async (id: string) => removeMut.mutateAsync(id),
  }
}
